import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Star } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ReviewModal Component
 * 
 * Modal for submitting trip reviews after booking completion.
 */
const ReviewModal = ({ 
  isOpen, 
  onClose, 
  booking, 
  reviewType, // 'host' or 'renter'
  api,
  onSuccess 
}) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setLoading(true);
    try {
      await api.post('/users/reviews', {
        booking_id: booking.booking_id,
        rating,
        comment
      });

      toast.success('Review submitted successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  const vehicleName = booking?.vehicle 
    ? `${booking.vehicle.make} ${booking.vehicle.model}` 
    : 'your trip';

  const title = reviewType === 'host' 
    ? 'How was your trip?' 
    : 'How was your renter?';

  const subtitle = reviewType === 'host'
    ? `Rate your experience with ${vehicleName}`
    : 'Rate your renter for this trip';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md mx-4 max-w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-center">{title}</DialogTitle>
          <p className="text-slate-500 text-center text-sm mt-1">{subtitle}</p>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Star Rating */}
          <div className="flex flex-col items-center">
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`star-${star}`}
                >
                  <Star 
                    className={`w-10 h-10 transition-colors ${
                      star <= (hoveredRating || rating)
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-slate-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-2">
              {rating === 0 ? 'Select a rating' : 
               rating === 1 ? 'Poor' :
               rating === 2 ? 'Fair' :
               rating === 3 ? 'Good' :
               rating === 4 ? 'Great' : 'Excellent'}
            </p>
          </div>

          {/* Comment */}
          <div>
            <Textarea
              placeholder="Share your experience (optional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="min-h-[100px] resize-none"
              data-testid="review-comment"
            />
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="ghost" 
            onClick={handleSkip}
            className="w-full sm:w-auto text-slate-500"
          >
            Skip for now
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={loading || rating === 0}
            className="w-full sm:w-auto bg-[#004B8D] hover:bg-[#003865]"
            data-testid="submit-review-btn"
          >
            {loading ? 'Submitting...' : 'Submit Review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewModal;
